import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { CreateDiscountInput, CreateDiscountResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook for creating a new discount.
 * Handles the API call, success/error states, and cache invalidation.
 */
export function useCreateDiscount() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<CreateDiscountResponse, Error, CreateDiscountInput>({
    mutationFn: async (input) => {
      return await rpcClient.accountsReceivable.discounts.create(input);
    },
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Discount created successfully',
        color: 'success',
        icon: 'check',
      });

      // Invalidate discount queries to refresh the data
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.discounts.list.queryKey({
          input: {},
        }),
      });

      // Also invalidate discount validation queries (for sales order drafts)
      // so the price status badges update immediately after discount creation
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.salesOrderDrafts.validateDiscounts.key(),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to create discount',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
